import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import Login from "./Login";
import MainDashboard from "./MainDashboard";
import Contact from "./Contact";
import HRDashboard from "./HRDashboard";
import EmployeeDashboard from "./EmployeeDashboard";
import CustodianDashboard from "./CustodianDashboardNew";
import KioskAttendancePage from "./KioskAttendancePage";
import AccountsPage from "./AccountsPage";
import ChangePassword from "./ChangePassword";
import { API_BASE } from "./utils/apiBase";

// Ensure cookies are sent with requests
axios.defaults.withCredentials = true;
// Optionally set base URL from env (falls back to local backend for dev on :3000)
axios.defaults.baseURL = API_BASE;

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [sessionVerified, setSessionVerified] = useState(false);
  const [apiSnack, setApiSnack] = useState({ open: false, message: "", severity: "warning" });
  const loginVerificationInFlight = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const routePath = location?.pathname || '/';
  const isProtectedRoute = routePath.startsWith('/dashboard') || routePath === '/change-password' || routePath === '/accounts';
  const isKioskAccount = (candidateUser = null) => {
    const normalizedRole = String(candidateUser?.role || "").toLowerCase();
    const employeeCode = String(candidateUser?.employeeid || candidateUser?.username || "").trim();
    return normalizedRole === "hr" && employeeCode === "00000002";
  };
  const getDashboardPath = (role, candidateUser = null) => {
    const normalized = String(role || '').toLowerCase();
    if (normalized === "hr" && isKioskAccount(candidateUser || user)) return "/dashboard/kiosk";
    if (normalized === "hr") return "/dashboard/hr";
    if (normalized === "admin") return "/dashboard/admin";
    if (normalized === "employee") return "/dashboard/employee";
    if (normalized === "custodian") return "/dashboard/custodian";
    return "/login";
  };
  const fetchVerifiedSessionUser = async () => {
    const res = await axios.get(API_BASE + '/api/me', { withCredentials: true });
    const me = res?.data?.user || null;
    if (!me) throw new Error('missing session user');
    return me;
  };

  useEffect(() => {
    (async () => {
      const publicRoutes = new Set(['/', '/contact', '/login']);
      if (publicRoutes.has(routePath)) {
        if (routePath === '/login' && user && sessionVerified) {
          setLoadingAuth(false);
          return;
        }
        setLoadingAuth(false);
        return;
      }
      if (isProtectedRoute && user && sessionVerified) {
        setLoadingAuth(false);
        return;
      }
      let cachedUser = null;
      try {
        cachedUser = localStorage.getItem('user');
      } catch (_) {}
      if (!cachedUser) {
        setUser(null);
        setSessionVerified(false);
        setLoadingAuth(false);
        try { navigate('/login', { replace: true }); } catch (_) {}
        return;
      }
      try {
        setSessionVerified(false);
        const me = await fetchVerifiedSessionUser();
        setUser(me);
        setSessionVerified(true);
        localStorage.setItem('user', JSON.stringify(me));
      } catch (err) {
        try { localStorage.removeItem('user'); } catch (_) {}
        setUser(null);
        setSessionVerified(false);
      } finally {
        setLoadingAuth(false);
      }
    })();
  }, [routePath, navigate, isProtectedRoute, user, sessionVerified]);

  // Global axios interceptor: only redirect on /api/me 401; show permission on 403
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        const isAuthMutation = url.includes('/api/login') || url.includes('/api/verify-otp') || url.includes('/api/resend-otp');
        if (status === 401 && isProtectedRoute && !isAuthMutation) {
          try { localStorage.removeItem('user'); } catch (_) {}
          setUser(null);
          setSessionVerified(false);
          try { navigate('/login', { replace: true }); } catch (e) {}
        }
        if (status === 403) {
          const msg = error?.response?.data?.error || error?.response?.data?.message || 'You do not have permission to access this resource.';
          setApiSnack({ open: true, message: msg, severity: 'warning' });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate, loadingAuth, isProtectedRoute]);

  // When login succeeds ? save to localStorage + redirect
  const handleLoginSuccess = async (loginUser = null) => {
    if (loginVerificationInFlight.current) return;
    loginVerificationInFlight.current = true;
    try {
      if (loginUser) {
        setUser(loginUser);
        setSessionVerified(false);
        localStorage.setItem("user", JSON.stringify(loginUser));
      }
      const me = await fetchVerifiedSessionUser();
      setUser(me);
      setSessionVerified(true);
      localStorage.setItem("user", JSON.stringify(me));
      const normalizedRole = String(me?.role || "").toLowerCase();
      const nextRoute = me?.must_change_password ? "/change-password" : getDashboardPath(normalizedRole, me);
      if (me?.must_change_password) {
        navigate("/change-password");
        return;
      }
      navigate(nextRoute);
    } catch (e) {
      try { localStorage.removeItem('user'); } catch (_) {}
      setUser(null);
      setSessionVerified(false);
      setApiSnack({ open: true, message: 'Session check failed. Please log in again.', severity: 'error' });
      try { navigate('/login'); } catch (err) {}
    } finally {
      loginVerificationInFlight.current = false;
    }
  };

  // Logout ? clear user + remove from localStorage
  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/api/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
    setUser(null);
    setSessionVerified(false);
    localStorage.removeItem("user");
    navigate("/login");
  };

  const normalizedRole = (user?.role || "").toLowerCase();
  const kioskUser = isKioskAccount(user);
  const canViewHr = !user?.must_change_password && normalizedRole === "hr" && sessionVerified && !kioskUser;
  const canViewAdmin = !user?.must_change_password && normalizedRole === "admin" && sessionVerified;
  const canViewEmployee = !user?.must_change_password && normalizedRole === "employee" && sessionVerified;
  const canViewCustodian = !user?.must_change_password && normalizedRole === "custodian" && sessionVerified;
  const canViewKiosk = !user?.must_change_password && normalizedRole === "hr" && sessionVerified && kioskUser;

  return (
    // Avoid routing/redirects until auth check completes
    loadingAuth || (isProtectedRoute && user && !sessionVerified) ? null : (
    <>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainDashboard />} /> {/* ? NEW */}
      <Route path="/contact" element={<Contact />} /> {/* ? NEW */}

      {/* Login Page */}
      <Route
        path="/login"
        element={
          user && sessionVerified ? (
            user.must_change_password ? (
              <Navigate to="/change-password" />
            ) : (
              <Navigate to={getDashboardPath(normalizedRole, user)} />
            )
          ) : (
            <Login onLoginSuccess={handleLoginSuccess} />
          )
        }
      />
      <Route
        path="/change-password"
        element={
          !user || !sessionVerified ? (
            <Navigate to="/login" />
          ) : user.must_change_password ? (
            <ChangePassword user={user} onPasswordChanged={handleLoginSuccess} />
          ) : (
            <Navigate to={getDashboardPath(normalizedRole, user)} />
          )
        }
      />

      {/* Dashboards */}
      <Route
        path="/dashboard/hr"
        element={
          user?.must_change_password ? (
            <Navigate to="/change-password" />
          ) : kioskUser ? (
            <Navigate to="/dashboard/kiosk" replace />
          ) : canViewHr ? (
            <HRDashboard user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/dashboard/kiosk"
        element={
          user?.must_change_password ? (
            <Navigate to="/change-password" />
          ) : canViewKiosk ? (
            <KioskAttendancePage user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          user?.must_change_password ? (
            <Navigate to="/change-password" />
          ) : canViewAdmin ? (
            <AccountsPage user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/dashboard/employee"
        element={
          user?.must_change_password ? (
            <Navigate to="/change-password" />
          ) : canViewEmployee ? (
            <EmployeeDashboard user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      >
      </Route>
      <Route
        path="/dashboard/custodian"
        element={
          user?.must_change_password ? (
            <Navigate to="/change-password" />
          ) : canViewCustodian ? (
            <CustodianDashboard user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

  <Route path="/accounts" element={user && sessionVerified && !kioskUser ? (<AccountsPage user={user} onLogout={handleLogout} />) : (<Navigate to={kioskUser ? "/dashboard/kiosk" : "/login"} />)} />

  {/* Default Route */}
  <Route path="*" element={<Navigate to="/" />} /> {/* ? changed from /login ? / */}
    </Routes>
    <Snackbar
      open={apiSnack.open}
      autoHideDuration={4000}
      onClose={() => setApiSnack({ open: false, message: '', severity: 'warning' })}
    >
      <Alert severity={apiSnack.severity || 'warning'}>{apiSnack.message}</Alert>
    </Snackbar>
    </>
    )
  );

}

export default AppWrapper;
